// frontend/src/components/V2TrialModal.js
// V2 シフトエンジン **試算 (dry-run)** モーダル。
// 既存 V1 ロジック (shiftAutoAssign.js) は一切触らず、別フローで V2 の挙動を確認する。
//
// 動作:
//   1. 管理者が「V2 試算」ボタンを押すとこのモーダルが開く
//   2. 年月 (+ 職種) を選んで「試算開始」
//   3. Supabase Edge Function v2-shift-dry-run を叩く
//   4. 結果を表示 (matrix / stats / 希望充足率 / 違反)
//   5. **DB には一切書き込まない**。 UI 上の確認のみ。

import React, { useState } from "react";
import { X, Zap, Clock, Users, Target, AlertTriangle, CheckCircle2, Shield } from "lucide-react";
import { supabase } from "../supabase";

const SUPABASE_URL = "https://tpxzotpgkcaioifgzlii.supabase.co";

function Row({ label, value, hint }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
      <span style={{ color: "#64748b", fontSize: 13 }}>{label}</span>
      <span style={{ color: "#0f172a", fontWeight: 600, fontSize: 13 }}>
        {value}
        {hint && <span style={{ color: "#94a3b8", fontWeight: 400, marginLeft: 6 }}>{hint}</span>}
      </span>
    </div>
  );
}

export default function V2TrialModal({ open, onClose, organizationId, defaultYear, defaultMonth, jobTypes = [] }) {
  const [year, setYear] = useState(defaultYear || new Date().getFullYear());
  const [month, setMonth] = useState(defaultMonth || (new Date().getMonth() + 1));
  const [jobTypeFilter, setJobTypeFilter] = useState("");
  const [timeoutSec, setTimeoutSec] = useState(60);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  // ユーザーが「緩和する制約」として選んだもの (再試算用)
  const [selectedRelaxations, setSelectedRelaxations] = useState(new Set());

  if (!open) return null;

  const run = async (relaxations = null) => {
    setRunning(true);
    setError("");
    setResult(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error("セッション切れです。再ログインしてください");

      const options = {
        timeout_sec: Number(timeoutSec),
        auto_diagnose_relaxations: !relaxations || relaxations.length === 0,
      };
      if (relaxations && relaxations.length > 0) {
        options.relaxations = relaxations;
      }

      const res = await fetch(`${SUPABASE_URL}/functions/v1/v2-shift-dry-run`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          organizationId,
          year: Number(year),
          month: Number(month),
          jobTypeFilter: jobTypeFilter || null,
          options,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.success === false) {
        throw new Error(data?.message || `V2 試算失敗 (status=${res.status})`);
      }
      setResult(data);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setRunning(false);
    }
  };

  const toggleRelaxation = (name) => {
    setSelectedRelaxations((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const rerunWithSelected = () => {
    if (selectedRelaxations.size === 0) return;
    run(Array.from(selectedRelaxations));
  };

  // hope_report が正確(matrix と希望の実比較)。 無ければ fallback で input_summary を使う。
  const hopeTotal = result?.hope_report?.total ?? result?.input_summary?.n_hopes ?? 0;
  const hopeAdhered = result?.hope_report?.honored ?? hopeTotal;
  const hopeRate = hopeTotal ? Math.round((hopeAdhered / hopeTotal) * 100) : 100;

  const statusColor = {
    OPTIMAL: "#059669",
    FEASIBLE: "#0ea5e9",
    INFEASIBLE: "#dc2626",
    MODEL_INVALID: "#dc2626",
    TIMEOUT: "#f59e0b",
    UNKNOWN: "#6b7280",
  }[result?.status] || "#6b7280";

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Zap size={20} color="#1d4ed8" />
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>V2 シフト試算</h2>
            <span style={styles.badge}>保存されません</span>
          </div>
          <button onClick={onClose} style={styles.closeBtn}><X size={18} /></button>
        </div>

        <div style={styles.body}>
          {/* 入力 */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>試算条件</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              <label style={styles.labelCol}>
                <span style={styles.labelText}>年</span>
                <input
                  type="number"
                  min={2024}
                  max={2030}
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  style={styles.input}
                />
              </label>
              <label style={styles.labelCol}>
                <span style={styles.labelText}>月</span>
                <select value={month} onChange={(e) => setMonth(e.target.value)} style={styles.input}>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>{m}月</option>
                  ))}
                </select>
              </label>
              <label style={styles.labelCol}>
                <span style={styles.labelText}>職種フィルタ</span>
                <select
                  value={jobTypeFilter}
                  onChange={(e) => setJobTypeFilter(e.target.value)}
                  style={styles.input}
                >
                  <option value="">全職種</option>
                  {jobTypes.map((jt) => (
                    <option key={jt.id} value={jt.id}>{jt.name}</option>
                  ))}
                </select>
              </label>
              <label style={styles.labelCol}>
                <span style={styles.labelText}>タイムアウト</span>
                <select
                  value={timeoutSec}
                  onChange={(e) => setTimeoutSec(e.target.value)}
                  style={styles.input}
                >
                  <option value={30}>30 秒</option>
                  <option value={60}>60 秒</option>
                  <option value={120}>120 秒</option>
                </select>
              </label>
            </div>
            <button onClick={() => run()} disabled={running} style={styles.runBtn}>
              {running ? "試算中…" : <>▶ 試算開始</>}
            </button>
            <p style={styles.hint}>
              本番 Supabase からスタッフ・パターン・希望を読み取り、V2 (CP-SAT) で解きます。
              <br />
              <strong>結果は画面に表示するだけで、DB には保存しません。</strong>
            </p>
          </div>

          {error && (
            <div style={styles.errorBox}>
              <AlertTriangle size={14} /> {error}
            </div>
          )}

          {result && (
            <>
              {result.auto_relaxed_fallback && (
                <div style={{
                  background: "#fef3c7", border: "1px solid #fde68a", color: "#92400e",
                  padding: "10px 12px", borderRadius: 10, fontSize: 13, lineHeight: 1.6,
                }}>
                  ⚠️ <strong>ハード制約では解けなかったため、全制約を自動緩和して配置を生成しました</strong>。
                  この結果は参考値です。 固定人数・希望休が物理的に競合している可能性があります。
                  下部の違反・警告 / 診断ヒントで原因を確認してください。
                </div>
              )}
              <div style={{ ...styles.card, borderLeft: `4px solid ${statusColor}` }}>
                <h3 style={styles.cardTitle}>
                  結果: <span style={{ color: statusColor }}>{result.status}</span>
                </h3>
                <Row label="計算時間" value={`${result.stats?.solve_time_sec}s`} hint={`(engine ${result.engine_latency_ms || "?"}ms)`} />
                <Row label="入力スタッフ" value={`${result.input_summary?.n_staff} 名`} />
                <Row label="対象日数" value={`${result.input_summary?.n_days} 日`} />
                <Row label="パターン数" value={`${result.input_summary?.n_patterns}`} />
                <Row label="希望件数" value={`${hopeTotal} 件`} />
                <Row label="希望充足率" value={`${hopeRate}%`} />
                <Row label="変数数" value={`${result.stats?.num_variables}`} />
                <Row label="制約数" value={`${result.stats?.num_constraints}`} />
                {result.stats?.objective_value != null && (
                  <Row label="目的関数" value={result.stats.objective_value} />
                )}
              </div>

              {result.diagnosis && (
                  (result.diagnosis.conflicting_hopes?.length > 0) ||
                  (result.diagnosis.recommended_relaxations?.length > 0) ||
                  (result.diagnosis.hints?.length > 0)
                ) && (
                <div style={styles.card}>
                  <h3 style={styles.cardTitle}><AlertTriangle size={14} color="#dc2626" /> 診断</h3>
                  {result.diagnosis.hints?.length > 0 && (
                    <>
                      <div style={styles.subTitle}>推奨対応</div>
                      <ul style={styles.ul}>
                        {result.diagnosis.hints.map((h, i) => (
                          <li key={i} style={{ fontSize: 12, lineHeight: 1.6 }}>{h}</li>
                        ))}
                      </ul>
                    </>
                  )}
                  {result.diagnosis.conflicting_hopes?.length > 0 && (
                    <>
                      <div style={styles.subTitle}>希望休の競合</div>
                      <ul style={styles.ul}>
                        {result.diagnosis.conflicting_hopes.slice(0, 10).map((c, i) => (
                          <li key={i} style={{ fontSize: 12, color: "#64748b" }}>{c}</li>
                        ))}
                      </ul>
                    </>
                  )}
                  {result.diagnosis.recommended_relaxations?.length > 0 && (
                    <>
                      <div style={styles.subTitle}>
                        緩和する制約を選んで再試算
                      </div>
                      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 8, lineHeight: 1.6 }}>
                        チェックした制約を「ソフト」扱いにして再度解きます。
                        重大度 (low → high) 順で、low は影響が小さいもの、high は組織の運用ルールの根幹に影響するものです。
                      </div>
                      <div style={{
                        background: "#ecfdf5", border: "1px solid #86efac", color: "#065f46",
                        padding: "6px 10px", borderRadius: 6, fontSize: 11, marginBottom: 8,
                      }}>
                        🛡 <strong>希望休</strong> と <strong>固定人数</strong> は常に 100% 守られます。
                        これらは V2 の最低保証なので UI から緩和できません。
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {result.diagnosis.recommended_relaxations.map((r) => (
                          <label
                            key={r.name}
                            style={{
                              display: "flex", alignItems: "flex-start", gap: 8,
                              padding: "6px 8px", borderRadius: 6,
                              background: selectedRelaxations.has(r.name) ? "#eff6ff" : "transparent",
                              cursor: "pointer",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedRelaxations.has(r.name)}
                              onChange={() => toggleRelaxation(r.name)}
                              style={{ marginTop: 2 }}
                            />
                            <div style={{ fontSize: 12, flex: 1 }}>
                              <span style={{ fontWeight: 600 }}>{r.label}</span>
                              <span
                                style={{
                                  marginLeft: 6, fontSize: 10, fontWeight: 700,
                                  color: r.severity === "high" ? "#b91c1c" : r.severity === "medium" ? "#b45309" : "#047857",
                                }}
                              >
                                [{r.severity}]
                              </span>
                              {r.makes_feasible_alone && (
                                <span style={{ marginLeft: 6, color: "#059669", fontSize: 11 }}>
                                  ✓ これ単体で解決
                                </span>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                      <button
                        onClick={rerunWithSelected}
                        disabled={running || selectedRelaxations.size === 0}
                        style={{
                          ...styles.runBtn,
                          marginTop: 10,
                          background: selectedRelaxations.size === 0
                            ? "#cbd5e1"
                            : "linear-gradient(135deg, #d97706, #b45309)",
                          cursor: selectedRelaxations.size === 0 ? "not-allowed" : "pointer",
                        }}
                      >
                        {running ? "再試算中…" : `選択した ${selectedRelaxations.size} 件を緩和して再試算`}
                      </button>
                    </>
                  )}
                </div>
              )}

              {result.violations?.length > 0 && (
                <div style={styles.card}>
                  <h3 style={styles.cardTitle}>違反・警告 ({result.violations.length})</h3>
                  <ul style={styles.ul}>
                    {result.violations.slice(0, 10).map((v, i) => (
                      <li key={i} style={{ fontSize: 12 }}>
                        <span style={{
                          color: v.severity === "error" ? "#dc2626" :
                                 v.severity === "warning" ? "#d97706" : "#64748b",
                          fontWeight: 600, marginRight: 6,
                        }}>[{v.severity}]</span>
                        {v.type}: {v.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.hope_report && result.hope_report.total > 0 && (
                <div style={styles.card}>
                  <h3 style={styles.cardTitle}>希望反映レポート</h3>
                  <Row label="全希望件数" value={`${result.hope_report.total} 件`} />
                  <Row label="反映された数" value={`${result.hope_report.honored} 件 (${Math.round(result.hope_report.honor_rate * 100)}%)`} />
                  {result.hope_report.by_type && Object.keys(result.hope_report.by_type).length > 0 && (
                    <Row label="希望の内訳" value={Object.entries(result.hope_report.by_type).map(([k, v]) => `${k}:${v}`).join(" / ")} />
                  )}
                  {result.hope_report.mismatch_reasons && Object.keys(result.hope_report.mismatch_reasons).length > 0 && (
                    <Row
                      label="不一致の理由別"
                      value={Object.entries(result.hope_report.mismatch_reasons).map(([k, v]) => {
                        const label = {
                          staff_inactive: "対象スタッフが非アクティブ",
                          unknown_mark: "希望が未知の種別",
                          date_out_of_range: "日付が対象月外",
                          malformed_key: "キー不正",
                          other: "候補が配置不可",
                        }[k] || k;
                        return `${label}:${v}`;
                      }).join(" / ")}
                    />
                  )}
                  {result.hope_report.mismatched_samples?.length > 0 && (
                    <>
                      <div style={{ ...styles.subTitle, color: "#dc2626" }}>
                        反映されなかった希望のサンプル (最大 20 件)
                      </div>
                      <table style={{ borderCollapse: "collapse", fontSize: 11, width: "100%" }}>
                        <thead>
                          <tr style={{ background: "#f8fafc", textAlign: "left" }}>
                            <th style={{ padding: 4 }}>employee / 日付</th>
                            <th style={{ padding: 4 }}>希望</th>
                            <th style={{ padding: 4 }}>→</th>
                            <th style={{ padding: 4 }}>実配置</th>
                            <th style={{ padding: 4 }}>理由</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.hope_report.mismatched_samples.map((m, i) => (
                            <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                              <td style={{ padding: 4, fontFamily: "monospace", fontSize: 10 }}>{m.key}</td>
                              <td style={{ padding: 4, fontWeight: 700 }}>{m.raw}</td>
                              <td style={{ padding: 4, color: "#64748b" }}>{m.expected}</td>
                              <td style={{ padding: 4, fontWeight: 700, color: "#dc2626" }}>{m.actual || "(なし)"}</td>
                              <td style={{ padding: 4, fontSize: 10, color: "#64748b" }}>{m.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                </div>
              )}

              {result.daily_pattern_counts && Object.keys(result.daily_pattern_counts).length > 0 && (
                <div style={{ ...styles.card, padding: 0 }}>
                  <h3 style={{ ...styles.cardTitle, padding: "14px 14px 10px" }}>
                    日別配置状況 (実配置 / 要件)
                  </h3>
                  <DailyCountsTable daily={result.daily_pattern_counts} />
                </div>
              )}

              {result.pattern_summary?.length > 0 && (
                <details style={styles.card}>
                  <summary style={{ cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                    シフトパターン設定の確認(読み取り結果) — クリックで展開
                  </summary>
                  <PatternSummaryTable patterns={result.pattern_summary} />
                </details>
              )}

              {result.matrix && Object.keys(result.matrix).length > 0 && (
                <div style={{ ...styles.card, padding: 0 }}>
                  <h3 style={{ ...styles.cardTitle, padding: "14px 14px 10px" }}>
                    シフト表プレビュー (全 {result.input_summary?.n_staff} 名 × {result.input_summary?.n_days} 日)
                  </h3>
                  <FullMatrixPreview matrix={result.matrix} />
                </div>
              )}

              <div style={styles.saveWarning}>
                <Shield size={14} />
                <span>
                  この結果は <strong>DB に保存されていません</strong>。
                  実運用するには既存の V1「自動割り当て」ボタンを使ってください。
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function DailyCountsTable({ daily }) {
  const dates = Object.keys(daily).sort();
  // 全パターンの集合を取る (__TOTAL__ 以外)
  const markSet = new Set();
  dates.forEach((d) => daily[d].forEach((r) => {
    if (r.mark !== "__TOTAL__") markSet.add(r.mark);
  }));
  const marks = Array.from(markSet);

  return (
    <div style={{ overflowX: "auto", maxHeight: "40vh", overflowY: "auto" }}>
      <table style={{ borderCollapse: "collapse", fontSize: 11, width: "100%" }}>
        <thead>
          <tr>
            <th style={{ ...stickyTh, left: 0, minWidth: 80 }}>日付</th>
            {marks.map((m) => (
              <th key={m} style={{ ...stickyThTop, minWidth: 50 }}>{m}</th>
            ))}
            <th style={{ ...stickyThTop, minWidth: 60, background: "#dbeafe", color: "#1e3a8a" }}>合計</th>
          </tr>
        </thead>
        <tbody>
          {dates.map((d) => {
            const rowMap = {};
            daily[d].forEach((r) => (rowMap[r.mark] = r));
            const totalRow = rowMap["__TOTAL__"];
            const dow = new Date(d).getDay();
            return (
              <tr key={d}>
                <td style={{ ...stickyTdLeft, color: dow === 0 ? "#dc2626" : dow === 6 ? "#2563eb" : "#334155" }}>
                  {d.slice(5)} ({"日月火水木金土"[dow]})
                </td>
                {marks.map((m) => {
                  const r = rowMap[m];
                  if (!r || (r.actual === 0 && r.required === 0)) {
                    return <td key={m} style={{ padding: "3px 6px", borderBottom: "1px solid #e5e7eb", color: "#cbd5e1", textAlign: "center" }}>-</td>;
                  }
                  const diff = r.diff;
                  const bg =
                    r.required === 0 ? "#f8fafc" :
                    r.is_fixed && diff !== 0 ? "#fee2e2" :
                    diff < 0 ? "#fef3c7" :
                    diff > 0 ? "#dbeafe" : "#dcfce7";
                  return (
                    <td key={m} style={{
                      padding: "3px 6px", borderBottom: "1px solid #e5e7eb",
                      textAlign: "center", background: bg, fontSize: 11,
                      fontWeight: diff !== 0 ? 700 : 500,
                    }}>
                      {r.actual}/{r.required}
                      <div style={{ fontSize: 8, color: "#64748b" }}>
                        {r.is_fixed ? "固定" : "柔軟"}
                      </div>
                    </td>
                  );
                })}
                {totalRow && (
                  <td style={{
                    padding: "3px 6px", borderBottom: "1px solid #e5e7eb",
                    textAlign: "center", fontSize: 11, fontWeight: 700,
                    background: totalRow.actual === totalRow.required ? "#dcfce7" : "#fee2e2",
                    color: totalRow.actual === totalRow.required ? "#065f46" : "#991b1b",
                  }}>
                    {totalRow.actual}/{totalRow.required}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ fontSize: 10, color: "#64748b", padding: "6px 14px", borderTop: "1px solid #e5e7eb" }}>
        表の見方: 「実配置 / 要件」。 🔴 固定で不一致 / 🟡 柔軟で下回る / 🟢 ちょうど / 🔵 過剰配置
      </div>
    </div>
  );
}

function PatternSummaryTable({ patterns }) {
  return (
    <table style={{ borderCollapse: "collapse", fontSize: 11, width: "100%", marginTop: 10 }}>
      <thead>
        <tr style={{ background: "#f8fafc", textAlign: "left" }}>
          <th style={{ padding: 4 }}>パターン</th>
          <th style={{ padding: 4 }}>mark</th>
          <th style={{ padding: 4 }}>required (曜日別)</th>
          <th style={{ padding: 4 }}>fixed (曜日別)</th>
          <th style={{ padding: 4 }}>max_same_consec</th>
          <th style={{ padding: 4 }}>post / forbidden</th>
        </tr>
      </thead>
      <tbody>
        {patterns.map((p) => {
          const wdNames = ["月", "火", "水", "木", "金", "土", "日"];
          const reqStr = Object.keys(p.required_by_weekday || {}).length > 0
            ? wdNames.map((n, i) => `${n}:${p.required_by_weekday?.[i] ?? "-"}`).join(" ")
            : `(スカラー: ${p.required_count})`;
          const fixedStr = Object.keys(p.is_fixed_by_weekday || {}).length > 0
            ? wdNames.map((n, i) => `${n}:${p.is_fixed_by_weekday?.[i] ? "F" : "f"}`).join("")
            : "-";
          return (
            <tr key={p.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
              <td style={{ padding: 4 }}>{p.name}</td>
              <td style={{ padding: 4, fontWeight: 700 }}>{p.mark}</td>
              <td style={{ padding: 4, fontFamily: "monospace", fontSize: 10 }}>{reqStr}</td>
              <td style={{ padding: 4, fontFamily: "monospace", fontSize: 10 }}>{fixedStr}</td>
              <td style={{ padding: 4 }}>{p.max_same_consecutive || "-"}</td>
              <td style={{ padding: 4, fontSize: 10 }}>
                {p.post_shift_ids?.length ? `→${p.post_shift_ids.length}` : ""}
                {p.forbidden_post_shift_ids?.length ? ` ✗${p.forbidden_post_shift_ids.length}` : ""}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function FullMatrixPreview({ matrix }) {
  // キー形式 "empId_YYYY-MM-DD" から empId / date を抽出し、全スタッフ × 全日の表を描画
  const empDateMap = {};
  Object.entries(matrix).forEach(([k, v]) => {
    const idx = k.lastIndexOf("_");
    const emp = k.slice(0, idx);
    const d = k.slice(idx + 1);
    (empDateMap[emp] ||= {})[d] = v;
  });
  const emps = Object.keys(empDateMap).sort();
  if (emps.length === 0) return <div style={{ padding: 14 }}>データなし</div>;

  // 全日付の集合
  const dateSet = new Set();
  emps.forEach((e) => Object.keys(empDateMap[e]).forEach((d) => dateSet.add(d)));
  const dates = Array.from(dateSet).sort();

  // mark に応じた色 (V1 の色相を踏襲)
  const colorFor = (mark) => {
    if (!mark) return "transparent";
    if (mark === "休") return "#fee2e2"; // 公休 (薄赤)
    if (mark === "明") return "#e0f2fe"; // 明け (薄青)
    if (mark === "有") return "#fef3c7"; // 有給 (薄黄)
    if (mark === "夏") return "#fef3c7";
    if (/夜|深|当|準/.test(mark)) return "#312e81"; // 夜勤系 (濃紺)
    if (/日/.test(mark)) return "#dcfce7"; // 日勤 (薄緑)
    return "#f1f5f9";
  };
  const textColorFor = (mark) => {
    if (/夜|深|当|準/.test(mark)) return "white";
    return "#0f172a";
  };

  return (
    <div style={{ overflowX: "auto", maxHeight: "60vh", overflowY: "auto" }}>
      <table style={{
        borderCollapse: "separate", borderSpacing: 0, fontSize: 10,
        fontFamily: "'SF Mono', 'Hiragino Sans', monospace",
      }}>
        <thead>
          <tr>
            <th style={{ ...stickyTh, left: 0, zIndex: 3 }}>Emp</th>
            {dates.map((d) => {
              const dayNum = Number(d.slice(-2));
              const dayDate = new Date(d);
              const dow = dayDate.getDay(); // 0=日
              const isWeekend = dow === 0 || dow === 6;
              return (
                <th
                  key={d}
                  style={{
                    ...stickyThTop,
                    background: isWeekend ? "#fef3c7" : "#f8fafc",
                    color: dow === 0 ? "#dc2626" : dow === 6 ? "#2563eb" : "#334155",
                  }}
                >
                  <div>{dayNum}</div>
                  <div style={{ fontSize: 8, opacity: 0.7 }}>{"日月火水木金土"[dow]}</div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {emps.map((e) => (
            <tr key={e}>
              <td style={{ ...stickyTdLeft }}>{e}</td>
              {dates.map((d) => {
                const mark = empDateMap[e][d] || "";
                return (
                  <td
                    key={d}
                    style={{
                      padding: "3px 4px",
                      borderBottom: "1px solid #e5e7eb",
                      borderRight: "1px solid #f1f5f9",
                      background: colorFor(mark),
                      color: textColorFor(mark),
                      textAlign: "center",
                      minWidth: 24,
                      fontWeight: mark && /夜|深|当|準/.test(mark) ? 700 : 500,
                    }}
                  >
                    {mark}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const stickyTh = {
  position: "sticky", top: 0, zIndex: 2,
  background: "#f8fafc", color: "#334155",
  padding: "6px 10px", borderBottom: "2px solid #cbd5e1",
  borderRight: "1px solid #e5e7eb",
  fontWeight: 700, textAlign: "left",
};
const stickyThTop = {
  position: "sticky", top: 0, zIndex: 1,
  padding: "4px 6px", borderBottom: "2px solid #cbd5e1",
  borderRight: "1px solid #e5e7eb",
  fontWeight: 700, textAlign: "center", minWidth: 28,
};
const stickyTdLeft = {
  position: "sticky", left: 0, zIndex: 1,
  background: "white",
  padding: "3px 10px", borderBottom: "1px solid #e5e7eb",
  borderRight: "2px solid #cbd5e1",
  fontWeight: 600, whiteSpace: "nowrap",
};

const styles = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.5)",
    display: "flex", alignItems: "flex-start", justifyContent: "center",
    zIndex: 1000, padding: "24px 12px", overflowY: "auto",
  },
  modal: {
    background: "white", borderRadius: 16, maxWidth: 1400, width: "100%",
    boxShadow: "0 25px 50px rgba(0,0,0,0.25)", maxHeight: "92vh",
    display: "flex", flexDirection: "column",
  },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "16px 20px", borderBottom: "1px solid #e5e7eb",
  },
  badge: {
    fontSize: 11, fontWeight: 700, color: "#9a3412",
    background: "#fed7aa", padding: "2px 8px", borderRadius: 999,
  },
  closeBtn: {
    background: "transparent", border: "none", color: "#64748b",
    cursor: "pointer", padding: 4, borderRadius: 6,
    display: "flex", alignItems: "center",
  },
  body: {
    padding: 20, overflowY: "auto", display: "flex", flexDirection: "column", gap: 14,
  },
  card: {
    background: "#f8fafc", borderRadius: 12, padding: 14,
    border: "1px solid #e5e7eb",
  },
  cardTitle: {
    fontSize: 14, fontWeight: 700, color: "#0f172a",
    margin: "0 0 10px", display: "flex", alignItems: "center", gap: 6,
  },
  subTitle: { fontSize: 12, fontWeight: 700, color: "#475569", margin: "8px 0 4px" },
  labelCol: { display: "flex", flexDirection: "column", gap: 4 },
  labelText: { fontSize: 11, color: "#64748b", fontWeight: 600 },
  input: {
    padding: "7px 10px", borderRadius: 8, border: "1px solid #cbd5e1",
    fontSize: 13, background: "white", minWidth: 90,
  },
  runBtn: {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "10px 18px", background: "linear-gradient(135deg, #1d4ed8, #1e40af)",
    color: "white", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700,
    cursor: "pointer", marginTop: 12,
  },
  hint: { fontSize: 11, color: "#64748b", lineHeight: 1.6, marginTop: 10 },
  errorBox: {
    background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c",
    padding: "10px 12px", borderRadius: 10, fontSize: 13,
    display: "flex", alignItems: "center", gap: 6,
  },
  saveWarning: {
    background: "#fef3c7", border: "1px solid #fde68a", color: "#92400e",
    padding: "10px 12px", borderRadius: 10, fontSize: 12, lineHeight: 1.6,
    display: "flex", alignItems: "center", gap: 8,
  },
  ul: { paddingLeft: 18, margin: "4px 0" },
  th: { textAlign: "left", padding: "4px 6px", borderBottom: "1px solid #e5e7eb", color: "#64748b" },
  td: { padding: "3px 6px", borderBottom: "1px solid #f1f5f9" },
};
